import { useSearchParams } from "react-router-dom";
import { ArrowLeft } from "lucide-react";

import { SupersetEmbed } from "../../../components/portfolio-risk-concentration/SupersetEmbed";
import {
  useSupersetDashboard,
  type SupersetFilters,
} from "../../../utils/portfolio-risk-concentration/api/superset-views";

/**
 * One embedded Superset dashboard, filling the tab.
 *
 * ── Why this page exists rather than a link to Superset ──────────────────────────────────
 * "Open in a new tab" cannot mean "open Superset". Two independent reasons, either one fatal:
 *
 *   1. Superset is fully login-gated and Athena users have no account there. The panel on
 *      Snapshot works only because prc-api mints a short-lived GUEST TOKEN that the embedded
 *      SDK hands to the iframe over postMessage. A plain URL carries no token, so it lands on
 *      Superset's sign-in page — which is exactly what was asked not to happen.
 *   2. Even past a login, the dashboard would be UNFILTERED. The page's Investor / Facility /
 *      Loans-Leases / as-of selections are not in the URL; they are RLS clauses baked into
 *      that guest token (app/router/alerts_config.py::_rls_clauses). A raw Superset tab would
 *      show a different, wider answer than the panel it was expanded from.
 *
 * So expanding opens another page of THIS app, which repeats the same guest-token mount at
 * full size. Superset stays login-gated, the token still grants one dashboard and one role
 * for a few minutes, and the filters travel because they are re-sent from the URL below.
 *
 * ── The filters come through the URL, and that is safe ───────────────────────────────────
 * They are plain values — "CAP1", "WAFRA", a date — never SQL. prc-api maps them through an
 * allowlist to build the clauses, so a hand-edited URL can pick a different legal filter (the
 * same thing the filter bar does) but cannot inject a clause or widen past what the token
 * grants. Anything unrecognised is dropped with a warning rather than guessed at.
 *
 * This page renders WITHOUT the steps navigation and footer — see PortfolioRiskConcentration,
 * which branches on the route before the chrome. The point of expanding is the extra screen.
 * The product access check still runs: it happens above that branch, so this route is as
 * entitlement-gated as every other.
 */
export default function SupersetExpandedPage() {
  const [searchParams] = useSearchParams();

  const dashboardKey = searchParams.get("key") ?? "summary";
  const title = searchParams.get("title") || "Portfolio Concentration Summary";

  // Rebuilt from the URL rather than passed through storage or postMessage: a new tab shares
  // neither with its opener, and the URL is also what makes the expanded view linkable and
  // refreshable. Empty params are dropped so they do not become empty RLS clauses.
  const filters: SupersetFilters = Object.fromEntries(
    (["scope", "facility", "product", "investor", "asOf"] as const)
      .map((k) => [k, searchParams.get(k) ?? undefined])
      .filter(([, v]) => !!v),
  );

  const dashboard = useSupersetDashboard(dashboardKey);

  return (
    <div className="flex h-screen flex-col bg-background p-4">
      {/* No browser Back reliance: this is a NEW tab, so its history is empty and Back is
          disabled. Closing it is the way out, and saying so beats a dead arrow. */}
      <div className="mb-3 flex shrink-0 items-center gap-2 text-xs text-muted-foreground">
        <ArrowLeft className="h-3.5 w-3.5" />
        Expanded view — close this tab to return to Snapshot.
      </div>

      <div className="min-h-0 flex-1">
        <SupersetEmbed
          title={title}
          uuid={dashboard.data?.uuid}
          supersetDomain={dashboard.data?.supersetDomain}
          loading={dashboard.isLoading}
          dashboardKey={dashboardKey}
          filters={filters}
          // The frame sizes itself from the flex parent instead of the 720px the panel uses.
          height="100%"
        />
      </div>
    </div>
  );
}
